import React, { useState, useEffect, useContext, useMemo } from "react";
import { GlobalContext } from "./GlobalContext";
import { AddEditCommitted } from "./lazyForms";
import CommonDataView from "./CommonDataView";
import useTabularDataManager from "./useTabularDataManager";
import useFieldOrder from "./useFieldOrder";
import MessageSelected from "./MessageSelected";
import { Button } from "@radix-ui/themes";
import FloatingDownloadButton from "./FloatingDownloadButton";
import CollectionTargetBar from "./CollectionTargetBar";
import DensityToggle from "./components/DensityToggle";
import { formatUGX } from "./UtilityFunctions";
import { isPastDue } from "./costFormulas";

function Credit({ cars, setCars, initialSelectedRow = "" , isLoading = false }){
    const ownSheetName = "Credit";

    const {
        modalVisible, setModalVisibility,
        messagesModalVisible, setMessagesModalVisible,
        sortOrder, sortData,
        filterObject, filterData,
        filteredDataArray
        } = useTabularDataManager(cars, setCars, ownSheetName);    

    return (
        <div className="flex flex-col h-full sheet-viewport" id="credit-content">
            {/* Above the scroll container, not inside it: that container scrolls
                horizontally with the table, so chips placed within it would slide
                off the side exactly like the column header they replace. Desktop
                only — MobileSheetView renders the same component in its toolbar. */}
            <SheetFilterChips
                filterObject={filterObject}
                filterData={filterData}
                className="hidden md:flex"
            />
            <Content isLoading={isLoading}
                modalVisible={modalVisible}
                setModalVisibility={setModalVisibility}
                filteredDataArray={filteredDataArray} setCars={setCars} sortData={sortData}
                sortOrder={sortOrder} parentSheetName={ownSheetName}
                filterData={filterData} filterObject={filterObject}
                ownSheetName={ownSheetName}
                initialSelectedRow={initialSelectedRow}
            />
            <Summary 
              setModalVisibility={setModalVisibility} 
              filteredDataArray={filteredDataArray} 
              setMessagesModalVisible={setMessagesModalVisible}/>
            {
              messagesModalVisible && 
                  <MessageSelected
                      onClose={()=>{setMessagesModalVisible(false)}}
                      recordsData={filteredDataArray}
                      sheetName={ownSheetName}
                  />
            }
        </div>
    )
}

function Content({ filteredDataArray, setCars, modalVisible, setModalVisibility,
   sortData, sortOrder, parentSheetName, filterData, filterObject, ownSheetName, initialSelectedRow = "", isLoading }){
    const [fieldOrder, setFieldOrder] = useFieldOrder(parentSheetName, filteredDataArray[0], ['Make', 'Model', 'ModelYear', 'CustomerName', 'TotalBalance', 'DueDate']);
    
    const [ currentVehicleIndex, setCurrentVehicle ] = useState();
    const openModalEdit = (vehicleId)=>{
        setCurrentVehicle(vehicleId);
        setModalVisibility({
            visible: true,
            mode: "edit"
        });
    }

    const [ selectedRow, setSelectedRow ] = useState(initialSelectedRow)

    useEffect(() => {
        if (initialSelectedRow) setSelectedRow(initialSelectedRow);
    }, [filteredDataArray, initialSelectedRow]);

    const selectRow = (rowIndex)=>{
        setSelectedRow(rowIndex)
    }

    // Closures reference no component state — stable for the component lifetime.
    const formattingRules = useMemo(() => ({
        Days: {
            formatByValue: (val)=>{
                if(val > 240 && val <= 270){
                    return {color: "orange", fontWeight: "bold"}
                }
                if(val > 270){
                    return {color: "red", fontWeight: "bold"}
                }
                return {color: "green", fontWeight: "bold"}
            }
        },
        DaysCommitted: {
            formatByValue: (val)=>{
                if(val > 30 && val <= 60){
                    return {color: "orange", fontWeight: "bold", textAlign: "center"}
                }
                if(val > 60){
                    return {color: "red", fontWeight: "bold", textAlign: "center"}
                }
                return {color: "green", fontWeight: "bold", textAlign: "center"}
            }
        }
    }), []);

    return (
        <div className="flex-grow overflow-auto min-h-0">
            <CommonDataView
                isLoading={isLoading}
                objectsArray={filteredDataArray}
                sortData={sortData}
                sortOrder={sortOrder}
                fieldOrder={fieldOrder}
                setFieldOrder={setFieldOrder}
                parentSheetName={parentSheetName}
                filterData={filterData}
                filterObject={filterObject}
                sheetName={ownSheetName}
                selectRow={selectRow}
                selectedRow={selectedRow}
                openModalEdit={openModalEdit}
                primaryKeyName={"VehicleID"}
                formattingRules={formattingRules}
                searchPlaceholder="Search credit sales…"
            />
            {
                modalVisible.visible &&  filteredDataArray[currentVehicleIndex] &&
                <AddEditCommitted 
                    formMode={modalVisible.mode} 
                    showModal={setModalVisibility} 
                    car={filteredDataArray[currentVehicleIndex]} 
                    parentSheetName={parentSheetName} 
                    //In add mode, read the origin that the user selected. In edit mode, read the origin from the record's origin field
                    origin={modalVisible.mode==="add" ? modalVisible.origin : filteredDataArray[currentVehicleIndex].Origin}
                    //The sheet as the user currently has it filtered and sorted.
                    //It gives the header its record stepper and its finder, so
                    //reaching the next deal no longer means closing the modal.
                    fleet={filteredDataArray}
                    fleetIndex={currentVehicleIndex}
                    onStep={setCurrentVehicle}
                />    
            }
            <FloatingDownloadButton 
                data={filteredDataArray}
                fieldOrder={fieldOrder}
                sheetName={ownSheetName}
                filterObject={filterObject}
            />     
        </div>
    )
}

function Summary({ filteredDataArray, setMessagesModalVisible }){
    const { can } = useContext(GlobalContext);
    return (
        /* sheet-actions-bar: same fixed-height non-wrapping row as Inventory's.
           Every child is rigid now, so when the row runs out of room it
           overflows and scrolls instead of crushing whichever child happens to
           be shrinkable — which used to be the collection meter, and only the
           collection meter. See the comment on SHELL in CollectionTargetBar.jsx
           and the .sheet-actions-bar rule in carstyles.css. */
        <div className="h-14 md:h-[80px] flex-none bg-white border-t flex gap-3 items-center px-4 sheet-actions-bar" id="credit-summary">
            {can('messages.send') && <Button
                id="message-filtered-button"
                className="ml-2"
                variant="soft"
                size="2"
                radius="medium"
                //onClick={()=>{setMessagesModalVisible(true)}}
                onClick={()=>{setMessagesModalVisible(true)}}
                title="Send messages to all customers in the current filtered list"
            >Send Messages</Button>}
            {/* Unlike the stat cards beside it, this one is dealership-wide and
                ignores the sheet's filters — see the header of
                CollectionTargetBar.jsx for why it has to be. */}
            <CollectionTargetBar />
            <CreditSalesStatsBar
              className="border-none shadow-none ml-2"
              filteredDataArray={filteredDataArray} />
            {/* Pinned right, as on Inventory and Committed — see the note in
                Inventory.jsx for why these three needed it adding by hand. */}
            <div className="hidden md:flex items-center ml-auto pr-1">
                <DensityToggle id="credit-density-toggle" />
            </div>
        </div>
    )
}

import { Card, Metric, Text } from "@tremor/react";
import SheetFilterChips from "./components/SheetFilterChips";

function CreditSalesStatsBar({ filteredDataArray }) {
  const [stats, setStats] = useState({
    totalVehicles: 0,
    totalValue: 0,
    totalPaid: 0,
    totalBalance: 0,
    overdueCount: 0,
    totalOverduePenalties: 0,
    totalTrackerCost: 0
  });

  useEffect(() => {
    if (!filteredDataArray || filteredDataArray.length === 0) return;

    // One pass, not six. This used to run five reduces and a filter over the same
    // array, so every keystroke in the search box walked the credit book six
    // times; on a large sheet that is the difference between one traversal and
    // six for numbers that are all read off the same row.
    //
    // Number() on every figure is not decoration: mysql2 returns DECIMAL as a
    // STRING, and a NULL numeric reaches the sheet as "" (normalizeValue in
    // UtilityFunctions.jsx turns it into one, so it is "" here rather than null).
    // Number("") is 0 and Number(null) is 0, so a deal with no recorded tracker
    // fee contributes nothing to the total instead of poisoning it with NaN —
    // which is right, because "no fee recorded" is not a cost of zero, it is the
    // absence of a figure, and the SHEET CELL still shows it blank.
    const totals = filteredDataArray.reduce((acc, car) => {
      acc.totalValue += Number(car.AgreedPrice) || 0;
      acc.totalPaid += Number(car.TotalPaid) || 0;
      acc.totalBalance += Number(car.TotalBalance) || 0;
      acc.totalOverduePenalties += Number(car.TotalPenalty) || 0;
      acc.totalTrackerCost += Number(car.TotalTrackerCost) || 0;
      //BOTH COLUMNS — see isPastDue. This tile counted a positive DaysOverdue
      //alone, which on a sheet where the counter never stopped meant every
      //customer who had ever run late stayed in the count after they had paid.
      //The dashboard's past-due card has always filtered on both, so the card
      //and the tile it opens were counting different things.
      if (isPastDue(car)) acc.overdueCount += 1;
      return acc;
    }, {
      totalValue: 0, totalPaid: 0, totalBalance: 0,
      totalOverduePenalties: 0, totalTrackerCost: 0, overdueCount: 0,
    });

    setStats({ totalVehicles: filteredDataArray.length, ...totals });
  }, [filteredDataArray]);

  //ABBREVIATED, and this is the vertical-overflow fix. "UGX 531,400,000" is
  //~150px of unbreakable-looking text; in a card narrower than that it wrapped
  //to a second line and pushed the card to 88px inside an 80px band, so the
  //figures were clipped top and bottom. Shortening the text is the fix rather
  //than widening the cards, because there is no width at which a nine-digit
  //figure and five cards and the meter all fit. The exact figure is on the
  //card's title attribute, so precision is one hover away.
  const money = (value) => formatUGX(value, { abbreviate: true });
  const exact = (value) => formatUGX(value);

  //Order is the one that was here before. `show` is what drops out as the band
  //narrows, cheapest information first: a count the sheet itself makes obvious,
  //then a cost that is usually zero. Outstanding and Overdue Customers survive
  //to the smallest band because they are the two the collections team acts on.
  //
  //The visibility class goes on a WRAPPER, not on the Card. `hidden` and `flex`
  //are both `display`, so putting them in one class string leaves the winner to
  //be decided by their order in Tailwind's output rather than by the breakpoint.
  const cards = [
    { key: 'count', label: 'Total Number', value: stats.totalVehicles,
      show: 'hidden xl:block' },
    { key: 'outstanding', label: 'Outstanding', value: money(stats.totalBalance),
      title: exact(stats.totalBalance), show: '' },
    { key: 'overdue', label: 'Overdue Customers', value: stats.overdueCount,
      dot: stats.overdueCount > 0, danger: stats.overdueCount > 0, show: '' },
    { key: 'penalties', label: 'Penalties', value: money(stats.totalOverduePenalties),
      title: exact(stats.totalOverduePenalties), show: 'hidden lg:block' },
    //What the trackers on these deals have cost so far: money already spent,
    //not owed to us, which is why it reads "Tracking Cost" and not a balance.
    { key: 'tracking', label: 'Tracking Cost', value: money(stats.totalTrackerCost),
      title: exact(stats.totalTrackerCost), show: 'hidden 2xl:block' },
  ];

  return (
    //A FLEX ROW, NOT A Grid. The Grid here was numItemsLg={6} holding FIVE
    //cards, so a whole 168px column — measured — was reserved for nothing while
    //the collection meter beside it was being squeezed to zero. It also carried
    //numItemsSm={3}, which below 1024px wrapped the five cards onto two rows,
    //128px of them inside an 80px band: that is the other half of the clipping,
    //and the reason cards appeared sliced through the middle.
    //
    //A flex row of fixed-size cards cannot do either. It is one line whose
    //height is stated, and cards leave the row instead of stacking.
    <div className="hidden md:flex items-center gap-2" id="credit-stats">
      {cards.map(({ key, label, value, title, dot, danger, show }) => (
        <div key={key} className={show}>
          <Card
            className="w-[134px] h-[60px] p-2 flex flex-col justify-center items-center"
            title={title}
          >
            <Text className="text-xs font-medium text-gray-500 flex items-center whitespace-nowrap">
              {label}
              {dot && <span className="ml-1 w-2 h-2 bg-red-500 rounded-full" />}
            </Text>
            <Metric className={`text-base whitespace-nowrap ${danger ? 'text-red-500' : ''}`}>
              {value}
            </Metric>
          </Card>
        </div>
      ))}
    </div>
  );
}

export default Credit;